import type { TufirmaEvent, TrackingOptions } from './analytics-events';
import { getAnalyticsContext } from './analytics-data-collector';
import { generateUUID } from '../utils/uuid';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

/**
 * 💎 ANALYTICS SERVICE: The unified dispatcher for all apps.
 * Now with Proxy support to bypass ad-blockers.
 */
export const AnalyticsService = {
  /**
   * Identifica al usuario en todos los trackers activos.
   */
  identify(userId: string, traits: Record<string, any> = {}) {
    // 1. PostHog Client (Standard)
    if (typeof window !== 'undefined') {
      posthog.identify(userId, traits);
    }

    // 2. Sentry (Si está inicializado)
    Sentry.setUser({ id: userId, ...traits });
  },

  /**
   * Registra propiedades que se enviarán en TODOS los eventos futuros.
   */
  setGlobalContext(properties: Record<string, any>) {
    if (typeof window !== 'undefined') {
      posthog.register(properties);
    }
  },

  /**
   * Trackeo universal con lógica de destino inteligente.
   * Por defecto, envía a PostHog vía PROXY (Servidor) para evitar bloqueos.
   */
  async track(eventName: TufirmaEvent, options: TrackingOptions = {}) {
    const { 
      targets = ['posthog'], 
      properties = {}, 
      salonId,
      userData,
      customData 
    } = options;

    // 🆔 ID Único de Evento para deduplicación (PostHog y Meta)
    const eventId = options.eventId || generateUUID();
    const contextData = getAnalyticsContext(salonId);

    // 👤 IDENTIDAD: PostHog requiere un distinct_id
    // Prioridad: 1. ID de PostHog (si ya existe) | 2. salonId (si se provee) | 3. sessionId (fallback)
    const phDistinctId = (typeof window !== 'undefined') 
      ? posthog.get_distinct_id?.() 
      : undefined;
    
    const distinctId = phDistinctId || salonId || contextData.sessionId || 'anonymous';
    
    // Merge with provided properties
    const enrichedProperties = {
      ...contextData,
      ...properties,
      distinct_id: distinctId,
      eventName,
      eventCategory: this.getEventCategory(eventName),
      // Añadimos el insert_id para que posthog-js lo use si puede
      $insert_id: eventId, 
    };

    // 1. POSTHOG CLIENT (Client-side)
    if (targets.includes('posthog') && typeof window !== 'undefined') {
      posthog.capture(eventName, enrichedProperties);
    }

    // 2. SENTRY BREADCRUMBS
    if (targets.includes('sentry_breadcrumb')) {
      Sentry.addBreadcrumb({
        category: 'analytics',
        message: `Event: ${eventName}`,
        data: { ...enrichedProperties, salonId },
        level: 'info',
      });
    }

    // Log para desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Analytics] 📊 Event: ${eventName}`, { targets, eventId });
    }
  },

  getEventCategory(eventName: TufirmaEvent): string {
    if (['service_selected', 'service_deselected', 'package_clicked', 'worker_selected', 'date_selected', 'time_selected'].includes(eventName as string)) {
      return 'booking_journey';
    }
    if (['whatsapp_clicked', 'location_opened', 'social_clicked', 'catalog_viewed'].includes(eventName as string)) {
      return 'engagement';
    }
    if (['booking_started', 'booking_completed', 'receipt_uploaded', 'qr_scanned'].includes(eventName as string)) {
      return 'conversion';
    }
    if (['modal_opened', 'modal_closed', 'form_submitted', 'cta_clicked'].includes(eventName as string)) {
      return 'interaction';
    }
    return 'general';
  },

  trackError(error: any, context: string) {
    console.error(`[Analytics] 🚨 Error in ${context}:`, error);

    try {
      Sentry.captureException(error, {
        extra: { context },
      });
    } catch (sentryError) {
      console.error('[Analytics] Failed to send error to Sentry:', sentryError);
    }

    this.track('form_submitted' as TufirmaEvent, {
      properties: { 
        status: 'error', 
        context, 
        errorMessage: error?.message || 'Unknown error' 
      },
      targets: ['posthog', 'sentry_breadcrumb']
    });
  }
};
